import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Heart, Home } from "lucide-react";
import axios from "axios";
import logger from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DonateSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/donate/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(data);
      } else if (data.status === "expired") {
        setStatus("expired");
      } else if (attempts < 5) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setStatus("pending");
        setPaymentData(data);
      }
    } catch (error) {
      logger.error("Error checking payment status:", error);
      if (attempts < 5) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    pollPaymentStatus();
  }, [pollPaymentStatus]);

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center" data-testid="donation-loading">
            <Loader2 className="w-16 h-16 text-soar-teal mx-auto mb-6 animate-spin" />
            <h2 className="font-heading font-bold text-2xl text-soar-blue mb-2">
              Processing Your Donation
            </h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-center" data-testid="donation-success">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-3xl text-soar-blue mb-4">
              Thank You for Your Donation!
            </h2>
            <p className="font-accent text-2xl text-soar-gold mb-4">
              "A light that lifts others."
            </p>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Your generous donation of ${paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '0.00'} will help us 
              continue empowering youth and serving our community.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              A confirmation email will be sent to you shortly.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/" data-testid="return-home-btn">
                <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90">
                  <Home className="w-4 h-4 mr-2" />
                  Return Home
                </Button>
              </Link>
              <Link to="/donate" data-testid="donate-again-btn">
                <Button variant="outline" className="rounded-full border-2 border-soar-gold text-soar-blue hover:bg-soar-gold">
                  <Heart className="w-4 h-4 mr-2" />
                  Donate Again
                </Button>
              </Link>
            </div>
          </div>
        );

      case "pending":
        return (
          <div className="text-center" data-testid="donation-pending">
            <Loader2 className="w-16 h-16 text-soar-gold mx-auto mb-6 animate-spin" />
            <h2 className="font-heading font-bold text-2xl text-soar-blue mb-4">
              Payment Processing
            </h2>
            <p className="text-muted-foreground mb-6">
              Your payment is still being processed. Please check your email for confirmation.
            </p>
            <Link to="/" data-testid="pending-home-btn">
              <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90">
                <Home className="w-4 h-4 mr-2" />
                Return Home
              </Button>
            </Link>
          </div>
        );

      case "expired":
        return (
          <div className="text-center" data-testid="donation-expired">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-yellow-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-soar-blue mb-4">
              Session Expired
            </h2>
            <p className="text-muted-foreground mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/donate" data-testid="retry-donate-btn">
              <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue">
                <Heart className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </Link>
          </div>
        );

      case "error":
      default:
        return (
          <div className="text-center" data-testid="donation-error">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-soar-blue mb-4">
              Something Went Wrong
            </h2>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your payment. If you were charged, please contact us.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/donate" data-testid="error-retry-btn">
                <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue">
                  Try Again
                </Button>
              </Link>
              <Link to="/contact" data-testid="error-contact-btn">
                <Button variant="outline" className="rounded-full border-2">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-[70vh] py-20 bg-gradient-to-br from-white via-soar-gold/5 to-soar-teal/5" data-testid="donate-success-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="rounded-3xl border-0 shadow-xl">
          <CardContent className="p-10">
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DonateSuccess;
